import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import {
  ButtonSize,
  MibBreakpointService,
  MibButtonDirective,
  MibIconComponent,
  MibTooltipDirective,
} from '@medinbox/angular';
import { Chat, ChatInfo, ChatService } from '@shared/components/chat';
import { CopyrightAndLegalLink } from '@shared/components/copyright-and-legal-link';
import { CASE_LIBRARY_SEARCH_CHAT_ID } from '@shared/models';
import { CaseSessionsService } from '@features/case-details/case-sessions.service';
import { CaseLibraryStore } from '../case-library-store';

@Component({
  selector: 'mai-search-tab',
  imports: [CopyrightAndLegalLink, Chat, MibButtonDirective, MibIconComponent, MibTooltipDirective],
  templateUrl: './search-tab.html',
  host: {
    class:
      'flex flex-col items-center overflow-hidden rounded-mib-lg not-expanded:rounded-none ' +
      'bg-mib-branding-900 text-mib-branding-50',
  },
  // Its own chat view, reading the library's conversation.
  providers: [ChatService],
})
export class SearchTab {
  private readonly chatService = inject(ChatService);
  private readonly router = inject(Router);
  private readonly library = inject(CaseLibraryStore);
  private readonly sessions = inject(CaseSessionsService);
  private readonly breakpointService = inject(MibBreakpointService);

  readonly chatInfo = input<ChatInfo>({
    chatId: CASE_LIBRARY_SEARCH_CHAT_ID,
    assistantIntro: 'I’m MIA, your Medinbox Intelligence Assistant.',
    assistantSubtext:
      'I know every case in your library: ask me to find one, compare procedures or draft a report.',
  });

  readonly hasMessages = this.chatService.hasMessages;
  protected readonly clearButtonSize = computed<ButtonSize>(() =>
    this.breakpointService.sizeClass() !== 'expanded' ? 'sm' : 'md',
  );

  clearChatHistory(): void {
    this.chatService.clearChatHistory();
  }

  manageTimeLinkClick(link: string): void {
    // A case the library lists is handed to its tab, named before it loads, as from its card.
    const id = /^\/video-details\/([^/?#]+)/.exec(link)?.[1];
    const listed = id ? this.library.cases()?.find((video) => video.id === id) : undefined;
    if (listed) {
      this.sessions.open(listed.id).foresee(listed);
    }
    this.router.navigateByUrl(link);
  }
}
